import { agentBetaAnnualRevenueSchema } from "schemas/agentBeta";
import { connectedChatRoutingPolicySchema } from "schemas/connectedChat";
import { currencySchema } from "schemas/project";
import { referralCodeSchema } from "schemas/referralCode";
import { shopifyIntegrationConfigSchema } from "schemas/shopifyIntegration";
import { workspaceDefaultProjectTypeSchema } from "schemas/workspaceDefaultProjectType";
import { z } from "zod";

export const workspaceRoleSchema = z
  .enum(["owner", "member", "viewer"])
  .describe("WorkspaceRole");

export const workspacePartnershipStatusTierSchema = z
  .enum(["affiliate", "silver", "gold", "platinum"])
  .describe("WorkspacePartnershipStatusTier");

export const workspaceUpdatesSchema = z
  .object({
    connectedChatRoutingPolicy: connectedChatRoutingPolicySchema.optional(),
    defaultProjectType: workspaceDefaultProjectTypeSchema.optional(),
    name: z.string().trim().min(1).optional(),
    // Writes are restricted to the signup revenue buckets so arbitrary
    // integers never land in the column.
    annualRevenue: agentBetaAnnualRevenueSchema.optional(),
  })
  .describe("WorkspaceUpdates");

export const workspaceUpdateSchema = workspaceUpdatesSchema
  .extend({ workspaceId: z.uuid() })
  .describe("WorkspaceUpdate");

const workspaceMembershipSchema = z.object({
  id: z.uuid(),
  role: workspaceRoleSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  archivedAt: z.coerce.date().nullish(),
  workspaceId: z.uuid(),
  userId: z.uuid(),
  user: z.object({
    id: z.string(),
    email: z.email(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
  }),
});

export const workspaceSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    domain: z.string(),
    partnershipAgreementSignedVersion: z.string().nullish(),
    partnershipAgreementSignedAt: z.coerce.date().nullish(),
    createdAt: z.coerce.date(),
    archivedAt: z.coerce.date().nullish(),
    partnershipAgreementSignedById: z.string().nullish(),
    kind: z.string().nullish(),
    paypalPayoutEmail: z.email().nullish(),
    overridePartnershipStatusTier:
      workspacePartnershipStatusTierSchema.nullish(),
    partnershipStatusTier: workspacePartnershipStatusTierSchema.nullish(),
    paymentProcessor: z.string().nullish(),
    isLegacyStripeWorkspace: z.boolean().nullish(),
    agentBillingEnabledAt: z.coerce.date().nullish(),
    // Start time of the booked founders onboarding call; null = not booked.
    onboardingCallScheduledAt: z.coerce.date().nullish(),
    defaultProjectType: workspaceDefaultProjectTypeSchema,
    initialDefaultProjectType: workspaceDefaultProjectTypeSchema,
    workspaceMemberships: z.array(workspaceMembershipSchema),
    timeZone: z.string().nullish(),
    // Customer-declared annual revenue in USD — the floor of one of the signup
    // buckets (agentBetaAnnualRevenueBuckets). null = unknown.
    annualRevenue: z.number().int().nullish(),
    currency: currencySchema.nullish(),
    referralCodes: z
      .array(referralCodeSchema.pick({ id: true, code: true }))
      .describe("ignoreImport:true"),
    isOnboarded: z.boolean(),
    hasShopifyIntegration: z.boolean(),
    hasShopifyActiveSubscription: z.boolean(),
    integrations: z
      .array(
        z.object({
          id: z.string(),
          shopifyIntegrationConfig: shopifyIntegrationConfigSchema.nullish(),
        }),
      )
      .nullish(),
    referralCode: referralCodeSchema
      .pick({ id: true, code: true })
      .nullish()
      .describe("ignoreImport:true"),
  })
  .describe("Workspace");

// NOTE (Kurt, 2024-12-09): Simplified version of the workspace schema containing only safe
// fields for unauthenticated consumption from the backend.
export const simpleWorkspaceSchema = workspaceSchema
  .pick({
    id: true,
    name: true,
  })
  .describe("SimpleWorkspace");

// Workspace deletion issue schemas
export const workspaceIssueTypeSchema = z
  .enum(["subscription", "projects", "experiments"])
  .describe("WorkspaceIssueType");

export const workspaceDeletionIssueSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("subscription"),
      count: z.number(),
      message: z.string(),
    }),
    z.object({
      type: z.literal("projects"),
      count: z.number(),
      message: z.string(),
    }),
    z.object({
      type: z.literal("experiments"),
      count: z.number(),
      message: z.string(),
    }),
  ])
  .describe("WorkspaceDeletionIssue");
